'use client';

import { useLogger } from 'next-axiom';
import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ElevarErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    // Error will be logged by the inner component using Axiom logger
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }

    return (
      <ElevarErrorBoundaryInner error={this.state.error}>
        {this.props.children}
      </ElevarErrorBoundaryInner>
    );
  }
}

// Inner component to use hooks for logging
function ElevarErrorBoundaryInner({
  children,
  error,
}: {
  children: ReactNode;
  error: Error | null;
}) {
  const logger = useLogger();

  React.useEffect(() => {
    if (error) {
      logger.error('Elevar tracking error', {
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name,
        },
        component: 'ElevarErrorBoundary',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent:
          typeof window !== 'undefined'
            ? window.navigator.userAgent
            : undefined,
      });
    }
  }, [error, logger]);

  return <>{children}</>;
}
